import AntIcon from "@/components/icons/AntIcon";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { appendSearchParamsToURL } from "@/utils/utils";
import { Card, Col, Row, Segmented, Tabs } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { useGetProfileCommissionsSettings } from "../../../common/api/queries";
import { rowGutter } from "../../../common/constants";
import { AgentCommissionsSummary } from "../../agent/components/AgentCommissionsSummary";
import { formatAgentAggregatedName, isAgentTipper } from "../../agent/utils";
import { useCurrentAgentAccount, useIsCurrentAgentTypePlus } from "../../auth/hooks";
import BailAccountMovementTableView from "../../commissions/bailaccount/components/views/BailAccountMovementTableView";
import { BailAccountSettingsView } from "../../commissions/bailaccount/components/views/BailAccountSettingsView";
import { DebtAccountMovementTable } from "../../commissions/debtaccount/components/tables/DebtAccountMovementTable";
import { CommissionsCategory } from "../../commissions/enums";
import { CommissionsOutputsAgentTable } from "../../commissions/outputs/components/tables/CommissionsOutputsAgentTable";
import { COMMISSIONS_PROFILE_TABS } from "../../globalsearch/utils";

export const UserCommissionsContainer = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [urlParams] = useSearchParams();
  const tabKey = urlParams.get("tab") ?? COMMISSIONS_PROFILE_TABS.OUTPUTS;

  const agent = useCurrentAgentAccount()?.agent;
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const isCurrentAgentTipper = !agent || isAgentTipper(agent);

  const [bailAccountCommissionsCategory, setBailAccountCommissionsCategory] = useState<CommissionsCategory>(
    isCurrentAgentTipper ? CommissionsCategory.PRIMARY_TIPPER : CommissionsCategory.PRIMARY_BROKER,
  );
  const [debtAccountCommissionsCategory, setDebtAccountCommissionsCategory] = useState<CommissionsCategory>(
    isCurrentAgentTipper ? CommissionsCategory.PRIMARY_TIPPER : CommissionsCategory.PRIMARY_BROKER,
  );

  const { data: commissionsSettings, isError } = useGetProfileCommissionsSettings(agent?.id);

  const handleTabKeyChange = (key: string) => {
    navigate(
      appendSearchParamsToURL({
        pageIndex: undefined,
        tab: key === COMMISSIONS_PROFILE_TABS.OUTPUTS ? undefined : key,
      }),
      { replace: true },
    );
  };

  return (
    <DisplayWrapper itemLoaded={!!commissionsSettings || !agent} isError={isError}>
      {commissionsSettings && agent && (
        <Card
          className="card-box"
          title={<h2>{`${t("user.titles.commissions")} | ${formatAgentAggregatedName(agent)}`}</h2>}
        >
          <AgentCommissionsSummary agent={agent} commissionsSettings={commissionsSettings} viewMode />

          <Tabs
            activeKey={tabKey}
            onChange={handleTabKeyChange}
            className="margin-top-large"
            destroyOnHidden
            items={[
              {
                key: COMMISSIONS_PROFILE_TABS.OUTPUTS,
                icon: <AntIcon type="euro-circle" />,
                label: t("commissions.settings.sections.outputs"),
                children: <CommissionsOutputsAgentTable agentId={agent.id} noActions />,
              },
              ...(isTopAgentPlus
                ? [
                    {
                      key: COMMISSIONS_PROFILE_TABS.BAIL_ACCOUNT,
                      icon: <AntIcon type="plus-square" />,
                      label: `${t("commissions.bailAccount.titles.page")}`,
                      children: (
                        <Row gutter={rowGutter}>
                          <Segmented
                            size="large"
                            className="margin-bottom-small"
                            options={[
                              {
                                label: t("commissions.enums.commissionsCategory_PLUS.PRIMARY_TIPPER"),
                                value: CommissionsCategory.PRIMARY_TIPPER,
                              },
                              {
                                label: t("commissions.enums.commissionsCategory_PLUS.PRIMARY_BROKER"),
                                value: CommissionsCategory.PRIMARY_BROKER,
                              },
                            ]}
                            value={bailAccountCommissionsCategory}
                            onChange={setBailAccountCommissionsCategory}
                          />

                          <Col span={24}>
                            <BailAccountSettingsView
                              settings={commissionsSettings.bailAccountSettings}
                              noSettingsLabel={t("commissions.bailAccount.helpers.noSettings")}
                            />
                          </Col>

                          <Col span={24}>
                            <BailAccountMovementTableView
                              agentId={agent.id}
                              category={bailAccountCommissionsCategory}
                            />
                          </Col>
                        </Row>
                      ),
                    },
                  ]
                : [
                    {
                      key: COMMISSIONS_PROFILE_TABS.BAIL_ACCOUNT,
                      icon: <AntIcon type="plus-square" />,
                      label: t("commissions.bailAccount.titles.page"),
                      children: (
                        <Row gutter={rowGutter}>
                          <Col span={24}>
                            <BailAccountSettingsView
                              settings={commissionsSettings.bailAccountSettings}
                              noSettingsLabel={t("commissions.bailAccount.helpers.noSettings")}
                            />
                          </Col>

                          <Col span={24}>
                            <BailAccountMovementTableView agentId={agent.id} category={CommissionsCategory.PRIMARY} />
                          </Col>
                        </Row>
                      ),
                    },
                  ]),
              ...(isTopAgentPlus
                ? [
                    {
                      key: COMMISSIONS_PROFILE_TABS.DEBT_ACCOUNT,
                      icon: <AntIcon type="minus-square" />,
                      label: `${t("commissions.debtAccount.titles.page")}`,
                      children: (
                        <>
                          <Segmented
                            size="large"
                            className="margin-bottom-small"
                            options={[
                              {
                                label: t("commissions.enums.commissionsCategory_PLUS.PRIMARY_TIPPER"),
                                value: CommissionsCategory.PRIMARY_TIPPER,
                              },
                              {
                                label: t("commissions.enums.commissionsCategory_PLUS.PRIMARY_BROKER"),
                                value: CommissionsCategory.PRIMARY_BROKER,
                              },
                              {
                                label: t("commissions.enums.commissionsCategory_PLUS.SECONDARY"),
                                value: CommissionsCategory.SECONDARY,
                              },
                            ]}
                            value={debtAccountCommissionsCategory}
                            onChange={setDebtAccountCommissionsCategory}
                          />

                          <DebtAccountMovementTable
                            agentId={agent.id}
                            showFilterSwitch={false}
                            category={debtAccountCommissionsCategory}
                          />
                        </>
                      ),
                    },
                  ]
                : [
                    {
                      key: COMMISSIONS_PROFILE_TABS.DEBT_ACCOUNT,
                      icon: <AntIcon type="minus-square" />,
                      label: t("commissions.debtAccount.titles.page"),
                      children: (
                        <DebtAccountMovementTable
                          agentId={agent.id}
                          showFilterSwitch={false}
                          category={CommissionsCategory.PRIMARY}
                        />
                      ),
                    },
                  ]),
            ].filter((item) => item !== undefined)}
          />
        </Card>
      )}
    </DisplayWrapper>
  );
};
